import React, { useState } from "react";
import styled from "styled-components";
import { Edit } from "lucide-react";
import Layout from "./layout";
import { userManagementData } from "../data/userManagementMockData";
import UserManagementModal from "../Modal/UserManagementModal";

const PageContainer = styled.div`
  font-family: 'Prompt', sans-serif;
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
`;

const FilterSection = styled.div`
  margin-bottom: 2rem;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FilterLabel = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #686868;
  min-width: 120px;
  padding-top: 0.5rem;
`;

const FilterInput = styled.input`
  padding: 0.4rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 12px;
  font-family: 'Prompt', sans-serif;
  width: 160px;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 0.5rem 2rem;
  flex: 1;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 12px;
  font-weight: 400;
  color: #686868;
  cursor: pointer;
  white-space: nowrap;
  min-width: fit-content;
`;

const Checkbox = styled.input`
  width: 0.875rem;
  height: 0.875rem;
  accent-color: #00B5B8;
  cursor: pointer;
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto 1fr auto;
  gap: 1rem;
  align-items: center;
`;

const SearchLabel = styled.label`
  font-size: 12px;
  font-weight: 400;
  color: #686868;
  white-space: nowrap;
`;

const SearchInput = styled.input`
  padding: 0.4rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 12px;
  font-family: 'Prompt', sans-serif;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #000000;
  color: #FFFFFF;
  border: none;
  border-radius: 0.375rem;
  padding: 0.4rem 1.5rem;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  font-family: 'Prompt', sans-serif;

  &:hover {
    background-color: #1F2937;
  }
`;

const CreateButton = styled.button`
  background-color: #000000;
  color: #FFFFFF;
  border: none;
  border-radius: 0.375rem;
  padding: 0.4rem 1.2rem;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  font-family: 'Prompt', sans-serif;

  &:hover {
    background-color: #1F2937;
  }
`;

const TableWrapper = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #F9FAFB;
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.5rem 0.3rem;
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #A3A3A3;
`;

const TableCell = styled.td`
  padding: 0.5rem 0.3rem;
  font-size: 12px;
  text-align: center;
  font-weight: 400;
  color: #686868;
`;

const CampaignBadge = styled.span`
  background-color: #1D87F0;
  color: #FFFFFF;
  padding: 0.2rem 0.5rem;
  border-radius: 0px;
  font-size: 11px;
  font-weight: 400;
  display: inline-block;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 0.25rem;
  color: #1D87F0;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PaginationButton = styled.button`
  padding: 0.4rem 0.6rem;
  border: none;
  background-color: ${props => props.active ? '#1976D2' : 'transparent'};
  color: ${props => props.active ? 'white' : '#1976D2'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.75rem;
  font-family: 'Prompt', sans-serif;
  min-width: 35px;
  text-decoration: ${props => props.active ? 'none' : 'underline'};

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const UserManagementPage = () => {
  const [filters, setFilters] = useState({
    หมอ: true,
    นักกำหนดอาหาร: false,
    เภสัชกร: false,
    พยาบาล: false,
    พยาบาลวิชาชีพ: false,
    นักวิชาการสาธารณสุข: false,
    ผู้ให้ความรู้โรคเบาหวาน: false,
    ผู้เชี่ยวชาญด้านการออกกำลังกาย: false,
    นักกายภาพบำบัด: false,
    เจ้าหน้าที่อื่น: false,
    ฯลฯ: false
  });

  const [searchName, setSearchName] = useState("");
  const [searchField, setSearchField] = useState("");
  const [searchHospital, setSearchHospital] = useState("");
  const [searchCompany, setSearchCompany] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 5;

  const handleFilterChange = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const handleSuperAdminChange = (id) => {
    // Handle super admin checkbox change
    console.log(`Toggle super admin for user ${id}`);
  };

  const handleSaveModal = (data) => {
    console.log('Saved data:', data);
    // Add your save logic here
  };

  const totalPages = Math.ceil(userManagementData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = userManagementData.slice(startIndex, endIndex);

  return (
    <Layout>
      <PageContainer>
        <HeaderSection>
          <CreateButton onClick={() => setIsModalOpen(true)}>
            เพิ่มที่ปรึกษา
          </CreateButton>
        </HeaderSection>

        <FilterSection>
          <FilterRow>
            <FilterLabel>ค้นหาจาก ชื่อ-สกุล</FilterLabel>
            <FilterInput
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
            <FilterLabel style={{ marginLeft: '1rem' }}>ประเภท</FilterLabel>
            <CheckboxGroup>
              {Object.keys(filters).map((filter) => (
                <CheckboxLabel key={filter}>
                  <Checkbox
                    type="checkbox"
                    checked={filters[filter]}
                    onChange={() => handleFilterChange(filter)}
                  />
                  {filter}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
          </FilterRow>

          <SearchRow>
            <SearchLabel>สาขาแพทย์</SearchLabel>
            <SearchInput
              type="text"
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
            />
            <SearchLabel>โรงพยาบาลวิชาชีพ</SearchLabel>
            <SearchInput
              type="text"
              value={searchHospital}
              onChange={(e) => setSearchHospital(e.target.value)}
            />
            <SearchLabel>บริษัทที่ดูแล</SearchLabel>
            <SearchInput
              type="text"
              value={searchCompany}
              onChange={(e) => setSearchCompany(e.target.value)}
            />
            <SearchButton>
              ค้นหา
            </SearchButton>
          </SearchRow>
        </FilterSection>

        <PaginationContainer>
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            previous
          </PaginationButton>
          {[...Array(totalPages)].map((_, index) => (
            <PaginationButton
              key={index + 1}
              active={currentPage === index + 1}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </PaginationButton>
          ))}
          <PaginationButton
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            next
          </PaginationButton>
        </PaginationContainer>

        <TableWrapper>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>ลำดับ</TableHeaderCell>
                <TableHeaderCell>รหัสที่ปรึกษา</TableHeaderCell>
                <TableHeaderCell>ชื่อผู้ใช้</TableHeaderCell>
                <TableHeaderCell>ชื่อ-สกุล</TableHeaderCell>
                <TableHeaderCell>super admin</TableHeaderCell>
                <TableHeaderCell>ประเภท</TableHeaderCell>
                <TableHeaderCell>สาขา</TableHeaderCell>
                <TableHeaderCell>โรงพยาบาลวิชาชีพ</TableHeaderCell>
                <TableHeaderCell>บริษัท</TableHeaderCell>
                <TableHeaderCell>แคมเปญ</TableHeaderCell>
                <TableHeaderCell>แก้ไข</TableHeaderCell>
              </tr>
            </TableHeader>
            <tbody>
              {currentData.map((user, index) => (
                <TableRow key={user.id}>
                  <TableCell>{startIndex + index + 1}</TableCell>
                  <TableCell>{user.code}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>
                    <Checkbox
                      type="checkbox"
                      checked={user.isSuperAdmin}
                      onChange={() => handleSuperAdminChange(user.id)}
                    />
                  </TableCell>
                  <TableCell>{user.type}</TableCell>
                  <TableCell>{user.field}</TableCell>
                  <TableCell>{user.hospital}</TableCell>
                  <TableCell>{user.company}</TableCell>
                  <TableCell>
                    <CampaignBadge>{user.campaign}</CampaignBadge>
                  </TableCell>
                  <TableCell>
                    <IconButton>
                      <Edit size={14} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </PageContainer>

      <UserManagementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveModal}
      />
    </Layout>
  );
};

export default UserManagementPage;